// Minimal, dependency-free Code 128 (code set B) barcode generator that returns
// an inline SVG string. Used by the Payment screen's "Print Barcode" label so a
// 1D laser scanner can read the pawn-ticket number. Kept self-contained (no npm
// dependency) so it also works when injected into a print popup window.
//
// Code set B covers printable ASCII 32–126 (digits, upper/lower letters, and
// common punctuation like "/" and "-"), which is all a voucher number needs.

// Bar/space width patterns for Code 128 values 0–106. Each string is a run of
// element widths (1–4 modules) starting with a bar and alternating; the final
// entry (106, Stop) carries the 2-module termination bar.
const PATTERNS = [
  "212222", "222122", "222221", "121223", "121322", "131222", "122213",
  "122312", "132212", "221213", "221312", "231212", "112232", "122132",
  "122231", "113222", "123122", "123221", "223211", "221132", "221231",
  "213212", "223112", "312131", "311222", "321122", "321221", "312212",
  "322112", "322211", "212123", "212321", "232121", "111323", "131123",
  "131321", "112313", "132113", "132311", "211313", "231113", "231311",
  "112133", "112331", "132131", "113123", "113321", "133121", "313121",
  "211331", "231131", "213113", "213311", "213131", "311123", "311321",
  "331121", "312113", "312311", "332111", "314111", "221411", "431111",
  "111224", "111422", "121124", "121421", "141122", "141221", "112214",
  "112412", "122114", "122411", "142112", "142211", "241211", "221114",
  "413111", "241112", "134111", "111242", "121142", "121241", "114212",
  "124112", "124211", "411212", "421112", "421211", "212141", "214121",
  "412121", "111143", "111341", "131141", "114113", "114311", "411113",
  "411311", "113141", "114131", "311141", "411131", "211412", "211214",
  "211232", "2331112",
];

const START_B = 104;
const STOP = 106;

// Sanitize to the Code 128-B printable range so an out-of-range char can never
// produce an invalid symbol.
const sanitize = (value) =>
  String(value == null ? "" : value)
    .split("")
    .filter((ch) => {
      const c = ch.charCodeAt(0);
      return c >= 32 && c <= 126;
    })
    .join("");

/**
 * Encode `value` as Code 128-B and return the bar geometry in *module units*
 * (1 module = one narrow element), quiet zone excluded.
 *
 * Split out from code128SVG so the barcode can also be drawn inside a larger
 * SVG — the label previewer places bars in the printer's own dot coordinate
 * space, which a self-contained <svg> string cannot participate in.
 *
 * @returns {{bars: Array<{x:number,w:number}>, width:number}} width = total modules
 */
export function code128Bars(value) {
  const text = sanitize(value);
  if (!text) return { bars: [], width: 0 };

  // Start B, data, checksum, Stop.
  const codes = [START_B];
  let checksum = START_B;
  for (let i = 0; i < text.length; i++) {
    const v = text.charCodeAt(i) - 32;
    codes.push(v);
    checksum += v * (i + 1);
  }
  codes.push(checksum % 103);
  codes.push(STOP);

  const bars = [];
  let x = 0;
  for (const code of codes) {
    let isBar = true;
    for (const ch of PATTERNS[code]) {
      const w = parseInt(ch, 10);
      if (isBar) bars.push({ x, w });
      x += w;
      isBar = !isBar;
    }
  }
  return { bars, width: x };
}

// ─── Code 39 ────────────────────────────────────────────────────────────────
// Needed because ZPL's ^B3 (Code 39) is still common in label designs shipped by
// tag-printer vendors. Each character is 9 elements — 5 bars and 4 spaces,
// alternating, starting with a bar — where "w" is `ratio` narrow-modules wide.
const CODE39 = {
  0: "nnnwwnwnn", 1: "wnnwnnnnw", 2: "nnwwnnnnw", 3: "wnwwnnnnn",
  4: "nnnwwnnnw", 5: "wnnwwnnnn", 6: "nnwwwnnnn", 7: "nnnwnnwnw",
  8: "wnnwnnwnn", 9: "nnwwnnwnn", A: "wnnnnwnnw", B: "nnwnnwnnw",
  C: "wnwnnwnnn", D: "nnnnwwnnw", E: "wnnnwwnnn", F: "nnwnwwnnn",
  G: "nnnnnwwnw", H: "wnnnnwwnn", I: "nnwnnwwnn", J: "nnnnwwwnn",
  K: "wnnnnnnww", L: "nnwnnnnww", M: "wnwnnnnwn", N: "nnnnwnnww",
  O: "wnnnwnnwn", P: "nnwnwnnwn", Q: "nnnnnnwww", R: "wnnnnnwwn",
  S: "nnwnnnwwn", T: "nnnnwnwwn", U: "wwnnnnnnw", V: "nwwnnnnnw",
  W: "wwwnnnnnn", X: "nwnnwnnnw", Y: "wwnnwnnnn", Z: "nwwnwnnnn",
  "-": "nwnnnnwnw", ".": "wwnnnnwnn", " ": "nwwnnnwnn", $: "nwnwnwnnn",
  "/": "nwnwnnnwn", "+": "nwnnnwnwn", "%": "nnnwnwnwn", "*": "nwnnwnwnn",
};

/**
 * Encode `value` as Code 39 and return bar geometry in narrow-module units.
 * Characters outside the Code 39 set are dropped rather than substituted — a
 * preview that silently invents characters would be worse than a short barcode.
 *
 * @param {string|number} value
 * @param {number} [ratio=3] wide-to-narrow ratio (ZPL's ^BY second parameter)
 */
export function code39Bars(value, ratio = 3) {
  const wide = Math.max(2, Math.min(3, Number(ratio) || 3));
  const body = String(value == null ? "" : value)
    .toUpperCase()
    .split("")
    .filter((ch) => ch !== "*" && CODE39[ch] !== undefined)
    .join("");
  if (!body) return { bars: [], width: 0 };

  const bars = [];
  let x = 0;
  for (const ch of `*${body}*`) {
    let isBar = true;
    for (const el of CODE39[ch]) {
      const w = el === "w" ? wide : 1;
      if (isBar) bars.push({ x, w });
      x += w;
      isBar = !isBar;
    }
    x += 1; // one narrow space between characters
  }
  return { bars, width: x };
}

/**
 * Encode `value` as a Code 128-B barcode and return an SVG string.
 * @param {string|number} value               text to encode (voucher/ticket no)
 * @param {object}   [opts]
 * @param {number}   [opts.moduleWidth=2]      px width of one narrow module
 * @param {number}   [opts.height=60]          bar height in px
 * @param {number}   [opts.quietModules=10]    white quiet-zone width in modules
 * @returns {string} inline <svg> markup (white background, black bars)
 */
export function code128SVG(value, opts = {}) {
  const moduleWidth = opts.moduleWidth || 2;
  const height = opts.height || 60;
  const quiet = opts.quietModules == null ? 10 : opts.quietModules;

  const { bars, width } = code128Bars(value);
  if (!width) {
    // Nothing to encode — return an empty, valid SVG so callers never crash.
    return `<svg xmlns="http://www.w3.org/2000/svg" width="1" height="${height}"></svg>`;
  }

  const rects = bars
    .map(
      (b) =>
        `<rect x="${(quiet + b.x) * moduleWidth}" y="0" width="${
          b.w * moduleWidth
        }" height="${height}" fill="#000"/>`,
    )
    .join("");

  const totalWidth = (width + quiet * 2) * moduleWidth;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${totalWidth}" height="${height}" ` +
    `viewBox="0 0 ${totalWidth} ${height}" shape-rendering="crispEdges">` +
    `<rect x="0" y="0" width="${totalWidth}" height="${height}" fill="#fff"/>` +
    rects +
    `</svg>`
  );
}
